/**
 * Auth Ready Service
 * Coordinates timing between server authentication and context providers.
 * 
 * Problem: Context providers (TitlePreference, TimeSettings, AdultContent, LinkSettings)
 * mount BEFORE AniListContext completes serverLogin(), causing 401 errors.
 * 
 * Solution: Contexts call waitForServerAuth() before making authenticated API calls.
 * AniListContext calls signalServerAuthReady() after serverLogin() completes.
 */

let resolveAuthReady;
let authReady = false; // Track whether auth has already completed
let authReadyPromise = new Promise(resolve => {
    resolveAuthReady = resolve;
});

/**
 * Wait for server auth to complete before making authenticated API calls.
 * Resolves immediately if auth is already complete.
 * Times out after 10 seconds to prevent indefinite blocking.
 * @returns {Promise<boolean>} true if auth succeeded, false if timed out
 */
export const waitForServerAuth = () => {
    // If auth already completed, resolve immediately — no timer needed
    if (authReady) return Promise.resolve(true);

    return Promise.race([
        authReadyPromise,
        new Promise(resolve => {
            const timeout = setTimeout(() => {
                console.warn('⚠️ Server auth wait timed out after 10s');
                resolve(false);
            }, 10000);
            // Clear timeout if auth resolves first
            authReadyPromise.then(() => clearTimeout(timeout));
        })
    ]);
};

/**
 * Signal that server auth is complete. All waiters will resolve.
 */
export const signalServerAuthReady = () => {
    authReady = true;
    resolveAuthReady(true);
};

/**
 * Reset the auth-ready state (e.g., on logout or token change).
 * Creates a fresh promise for the next auth cycle.
 */
export const resetAuthReady = () => {
    authReady = false;
    authReadyPromise = new Promise(resolve => {
        resolveAuthReady = resolve;
    });
};
